import axios, { AxiosResponse, AxiosError } from 'axios';
import { ApiResponse, ApiError, Therapist, Student, Schedule, Assignment, SessionConfig, SessionTimeSlotsByDay, GuestStudent } from '../types';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('API Success Response:', {
      url: response.config?.url,
      method: response.config?.method,
      status: response.status,
      data: response.data
    });
    return response;
  },
  (error: AxiosError) => {
    const apiError: ApiError = {
      error: 'An error occurred',
      status: error.response?.status,
    };

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.warn('401 Unauthorized - clearing token and redirecting to login');
      localStorage.removeItem('accessToken');
      
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
      
      apiError.error = 'Session expired. Please login again.';
    } else if (error.response?.data) {
      const errorData = error.response.data as any;
      apiError.error = errorData.error || errorData.message || 'An error occurred';
      apiError.details = errorData.details;
    } else if (error.request) {
      apiError.error = 'Network error - please check your connection';
    } else {
      apiError.error = error.message || 'An unexpected error occurred';
    }

    // Enhanced error logging
    console.error('=== API ERROR DETAILS ===');
    console.error('URL:', error.config?.url);
    console.error('Method:', error.config?.method);
    console.error('Request Data:', error.config?.data);
    console.error('Status:', error.response?.status);
    console.error('Response Data:', error.response?.data);
    console.error('Error Message:', apiError.error);
    console.error('Error Details:', apiError.details);
    console.error('Full Error Object:', error);

    return Promise.reject(apiError);
  }
);

// Generic API methods
export const apiClient = {
  get: <T>(url: string): Promise<ApiResponse<T>> =>
    api.get(url).then((response) => response.data),
  
  post: <T>(url: string, data?: any): Promise<ApiResponse<T>> =>
    api.post(url, data).then((response) => response.data),
  
  put: <T>(url: string, data?: any): Promise<ApiResponse<T>> =>
    api.put(url, data).then((response) => response.data),
  
  delete: <T>(url: string): Promise<ApiResponse<T>> =>
    api.delete(url).then((response) => response.data),
};

// Helper function to transform therapist data from API format to frontend format
const transformTherapist = (apiTherapist: any): Therapist => ({
  id: apiTherapist.id,
  name: apiTherapist.name,
  specialization: apiTherapist.specialization,
  qualification: apiTherapist.qualification,
  designation: apiTherapist.designation,
  email: apiTherapist.email,
  phone: apiTherapist.phone,
  is_active: Boolean(apiTherapist.is_active),
  createdAt: apiTherapist.created_at,
  updatedAt: apiTherapist.updated_at,
});

// Helper function to transform therapist data from frontend format to API format
const transformTherapistForApi = (therapist: Partial<Therapist>): any => ({
  name: therapist.name,
  specialization: therapist.specialization,
  qualification: therapist.qualification,
  designation: therapist.designation,
  email: therapist.email,
  phone: therapist.phone,
  is_active: therapist.is_active,
});

// Helper function to transform student data from API format to frontend format
const transformStudent = (apiStudent: any): Student => ({
  id: apiStudent.id,
  first_name: apiStudent.first_name,
  last_name: apiStudent.last_name,
  fathers_name: apiStudent.fathers_name,
  mothers_name: apiStudent.mothers_name,
  date_of_birth: apiStudent.date_of_birth,
  nationality: apiStudent.nationality,
  mother_tongue: apiStudent.mother_tongue,
  emirate: apiStudent.emirate,
  emirates_id: apiStudent.emirates_id,
  primary_contact: apiStudent.primary_contact,
  secondary_contact: apiStudent.secondary_contact,
  email: apiStudent.email,
  joining_date: apiStudent.joining_date,
  grade_level: apiStudent.grade_level,
  therapy_needs: apiStudent.therapy_needs,
  special_requirements: apiStudent.special_requirements,
  is_active: Boolean(apiStudent.is_active),
  created_at: apiStudent.created_at,
  updated_at: apiStudent.updated_at,
});

// Helper function to transform student data from frontend format to API format
const transformStudentForApi = (student: Partial<Student>): any => ({
  first_name: student.first_name,
  last_name: student.last_name,
  fathers_name: student.fathers_name,
  mothers_name: student.mothers_name,
  date_of_birth: student.date_of_birth,
  nationality: student.nationality,
  mother_tongue: student.mother_tongue,
  emirate: student.emirate,
  emirates_id: student.emirates_id,
  primary_contact: student.primary_contact,
  secondary_contact: student.secondary_contact,
  email: student.email,
  joining_date: student.joining_date,
  grade_level: student.grade_level,
  therapy_needs: student.therapy_needs,
  special_requirements: student.special_requirements,
  is_active: student.is_active,
});

const transformGuestStudent = (apiGuestStudent: any): GuestStudent => ({
  id: apiGuestStudent.id,
  fullName: apiGuestStudent.full_name,
  contactInfo: apiGuestStudent.contact_info || undefined,
  notes: apiGuestStudent.notes || undefined,
  createdAt: apiGuestStudent.created_at
});

// Therapist API
export const therapistApi = {
  getAll: async (params?: { page?: number; limit?: number; active?: boolean; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    const response = await apiClient.get<{ therapists: any[]; pagination: any }>(`/therapists${queryString ? '?' + queryString : ''}`);
    return {
      ...response,
      data: {
        ...response.data,
        therapists: response.data.therapists.map(transformTherapist)
      }
    };
  },
  
  getById: async (id: number) => {
    const response = await apiClient.get<{ therapist: any }>(`/therapists/${id}`);
    return {
      ...response,
      data: {
        therapist: transformTherapist(response.data.therapist)
      }
    };
  },
  
  create: async (data: Partial<Therapist>) => {
    const response = await apiClient.post<{ therapist: any }>('/therapists', transformTherapistForApi(data));
    return {
      ...response,
      data: {
        therapist: transformTherapist(response.data.therapist)
      }
    };
  },
  
  update: async (id: number, data: Partial<Therapist>) => {
    const response = await apiClient.put<{ therapist: any }>(`/therapists/${id}`, transformTherapistForApi(data));
    return {
      ...response,
      data: {
        therapist: transformTherapist(response.data.therapist)
      }
    };
  },
  
  delete: (id: number, force?: boolean) =>
    apiClient.delete(`/therapists/${id}${force ? '?force=true' : ''}`),
};

// Student API
export const studentApi = {
  getAll: async (params?: { page?: number; limit?: number; active?: boolean; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    const response = await apiClient.get<{ students: any[]; pagination: any }>(`/students${queryString ? '?' + queryString : ''}`);
    return {
      ...response,
      data: {
        ...response.data,
        students: response.data.students.map(transformStudent)
      }
    };
  },
  
  getById: async (id: number) => {
    const response = await apiClient.get<{ student: any; therapyHistory: any[] }>(`/students/${id}`);
    return {
      ...response,
      data: {
        ...response.data,
        student: transformStudent(response.data.student)
      }
    };
  },
  
  create: async (data: Partial<Student>) => {
    const response = await apiClient.post<{ student: any }>('/students', transformStudentForApi(data));
    return {
      ...response,
      data: {
        student: transformStudent(response.data.student)
      }
    };
  },
  
  update: async (id: number, data: Partial<Student>) => {
    const response = await apiClient.put<{ student: any }>(`/students/${id}`, transformStudentForApi(data));
    return {
      ...response,
      data: {
        student: transformStudent(response.data.student)
      }
    };
  },
  
  delete: (id: number, force?: boolean) =>
    apiClient.delete(`/students/${id}${force ? '?force=true' : ''}`),
  
  getGradeLevels: () =>
    apiClient.get<{ grade_levels: string[] }>('/students/meta/grade-levels'),

  getSessionStatistics: async (params?: {
    startDate?: string;
    endDate?: string;
    active?: string;
    therapistId?: number;
    scheduleId?: number
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    return apiClient.get<any>(`/students/analytics/session-statistics${queryString ? '?' + queryString : ''}`);
  },
};

// Guest Student API
export const guestStudentApi = {
  getAll: async (params?: { search?: string; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    const response = await apiClient.get<{ guestStudents: any[] }>(
      `/guest-students${queryString ? `?${queryString}` : ''}`
    );
    return {
      ...response,
      data: {
        guestStudents: response.data.guestStudents.map(transformGuestStudent)
      }
    };
  },

  getById: async (id: number) => {
    const response = await apiClient.get<{ guestStudent: any }>(`/guest-students/${id}`);
    return {
      ...response,
      data: {
        guestStudent: transformGuestStudent(response.data.guestStudent)
      }
    };
  },

  create: async (data: { fullName: string; contactInfo?: string; notes?: string }) => {
    const response = await apiClient.post<{ guestStudent: any }>('/guest-students', data);
    return {
      ...response,
      data: {
        guestStudent: transformGuestStudent(response.data.guestStudent)
      }
    };
  },

  update: async (id: number, data: { fullName?: string; contactInfo?: string; notes?: string }) => {
    const response = await apiClient.put<{ guestStudent: any }>(`/guest-students/${id}`, data);
    return {
      ...response,
      data: {
        guestStudent: transformGuestStudent(response.data.guestStudent)
      }
    };
  },

  delete: (id: number) => apiClient.delete(`/guest-students/${id}`)
};

// Helper function to transform schedule data from API format to frontend format
const transformSchedule = (apiSchedule: any): Schedule => ({
  id: apiSchedule.id,
  therapistId: apiSchedule.therapist_id || apiSchedule.therapistId,
  weekStartDate: apiSchedule.week_start_date || apiSchedule.weekStartDate,
  weekEndDate: apiSchedule.week_end_date || apiSchedule.weekEndDate,
  sessionConfigId: apiSchedule.session_config_id || apiSchedule.sessionConfigId,
  workingHoursStart: apiSchedule.working_hours_start || apiSchedule.workingHoursStart,
  workingHoursEnd: apiSchedule.working_hours_end || apiSchedule.workingHoursEnd,
  sessionDuration: apiSchedule.session_duration || apiSchedule.sessionDuration,
  therapistName: apiSchedule.therapist_name || apiSchedule.therapistName,
  therapistSpecialization: apiSchedule.therapist_specialization || apiSchedule.therapistSpecialization,
  configName: apiSchedule.config_name || apiSchedule.configName,
  createdBy: apiSchedule.created_by || apiSchedule.createdBy,
  createdAt: apiSchedule.created_at || apiSchedule.createdAt,
  updatedAt: apiSchedule.updated_at || apiSchedule.updatedAt,
  assignments: apiSchedule.assignments,
  timeSlots: apiSchedule.timeSlots,
  sessionConfig: apiSchedule.sessionConfig, // ✅ CRITICAL FIX: Include session config data
});

// Helper function to transform schedule data from frontend format to API format
const transformScheduleForApi = (schedule: any): any => ({
  therapistId: schedule.therapistId,
  weekStartDate: schedule.weekStartDate,
  weekEndDate: schedule.weekEndDate,
  sessionConfigId: schedule.sessionConfigId,
  workingHoursStart: schedule.workingHoursStart,
  workingHoursEnd: schedule.workingHoursEnd,
  sessionDuration: schedule.sessionDuration,
  assignments: schedule.assignments,
});

// Schedule API
export const scheduleApi = {
  getAll: async (params?: { startDate?: string; endDate?: string; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    const response = await apiClient.get<{ schedules: any[]; pagination: any }>(`/schedules${queryString ? '?' + queryString : ''}`);
    return {
      ...response,
      data: {
        ...response.data,
        schedules: response.data.schedules.map(transformSchedule)
      }
    };
  },
  
  getById: async (id: number) => {
    const response = await apiClient.get<{ schedule: any }>(`/schedules/${id}`);
    return {
      ...response,
      data: {
        schedule: transformSchedule(response.data.schedule)
      }
    };
  },
  
  create: async (data: any) => {
    const response = await apiClient.post<{ schedule: any }>('/schedules', transformScheduleForApi(data));
    return {
      ...response,
      data: {
        schedule: transformSchedule(response.data.schedule)
      }
    };
  },
  
  update: async (id: number, data: Partial<Schedule>) => {
    const response = await apiClient.put<{ schedule: any }>(`/schedules/${id}`, transformScheduleForApi(data));
    return {
      ...response,
      data: {
        schedule: transformSchedule(response.data.schedule)
      }
    };
  },
  
  delete: (id: number) =>
    apiClient.delete(`/schedules/${id}`),
  
  getConflicts: (id: number) =>
    apiClient.get<{ conflicts: any }>(`/schedules/${id}/conflicts`),
};

// Session Config API
export const sessionConfigApi = {
  getAll: () =>
    apiClient.get<{ configs: SessionConfig[] }>('/session-configs'),
  
  getById: (id: number) =>
    apiClient.get<{ config: SessionConfig; timeSlots: SessionTimeSlotsByDay }>(`/session-configs/${id}`),
  
  getDefault: () =>
    apiClient.get<{ config: SessionConfig; timeSlots: SessionTimeSlotsByDay }>('/session-configs/default/config'),
  
  create: (data: { name: string; description?: string; timeSlots: SessionTimeSlotsByDay }) =>
    apiClient.post<{ config: SessionConfig }>('/session-configs', data),
  
  update: (id: number, data: { name?: string; description?: string; timeSlots?: SessionTimeSlotsByDay }) =>
    apiClient.put<{ config: SessionConfig }>(`/session-configs/${id}`, data),
  
  delete: (id: number) =>
    apiClient.delete(`/session-configs/${id}`),
};

// Helper function to transform assignment data from API format to frontend format
const transformAssignment = (apiAssignment: any): Assignment => {
  const rawGuest =
    apiAssignment.guest_student ||
    apiAssignment.guestStudent ||
    (apiAssignment.guest_student_name
      ? {
          id: apiAssignment.guest_student_id,
          full_name: apiAssignment.guest_student_name,
          contact_info: apiAssignment.guest_student_contact,
          notes: apiAssignment.guest_student_notes
        }
      : null);

  let guestStudent;
  if (rawGuest) {
    const parsed =
      typeof rawGuest === 'string'
        ? (() => {
            try {
              return JSON.parse(rawGuest);
            } catch {
              return null;
            }
          })()
        : rawGuest;

    if (parsed) {
      guestStudent = {
        id: parsed.id ?? apiAssignment.guest_student_id ?? null,
        fullName: parsed.full_name ?? parsed.fullName ?? '',
        contactInfo: parsed.contact_info ?? parsed.contactInfo ?? undefined,
        notes: parsed.notes ?? undefined
      };
    }
  }

  return {
    id: apiAssignment.id,
    scheduleId: apiAssignment.schedule_id,
    therapistId: apiAssignment.therapist_id,
    studentId: apiAssignment.student_id,
    guestStudentId: guestStudent?.id ?? apiAssignment.guest_student_id ?? undefined,
    participantType: (() => {
      if (apiAssignment.assignment_type === 'break') return 'break';
      if (guestStudent?.id || apiAssignment.guest_student_id) return 'guest';
      if (apiAssignment.student_id) return 'registered';
      return undefined;
    })(),
    assignmentDate: apiAssignment.assignment_date,
    startTime: apiAssignment.start_time,
    endTime: apiAssignment.end_time,
    dayOfWeek: apiAssignment.day_of_week,
    assignmentType: apiAssignment.assignment_type,
    activityName: apiAssignment.activity_name,
    notes: apiAssignment.notes,
    colorOverride: apiAssignment.color_override,
    createdAt: apiAssignment.created_at,
    updatedAt: apiAssignment.updated_at,
    therapist: apiAssignment.therapist_name
      ? ({
          id: apiAssignment.therapist_id,
          name: apiAssignment.therapist_name,
          specialization: apiAssignment.specialization,
          colorCode: apiAssignment.color_code,
          is_active: true
        } as Therapist)
      : undefined,
    student: apiAssignment.student_name
      ? ({
          id: apiAssignment.student_id,
          first_name: apiAssignment.student_name.split(' ')[0] || '',
          last_name: apiAssignment.student_name.split(' ').slice(1).join(' ') || '',
          grade_level: apiAssignment.grade_level,
          is_active: true
        } as Student)
      : undefined,
    guestStudent: guestStudent && guestStudent.fullName
      ? {
          id: guestStudent.id ?? undefined,
          fullName: guestStudent.fullName,
          contactInfo: guestStudent.contactInfo,
          notes: guestStudent.notes
        }
      : undefined
  };
};

// Helper function to transform assignment data from frontend format to API format
const transformAssignmentForApi = (assignment: Partial<Assignment>): any => {
  const transformed: any = {
    schedule_id: assignment.scheduleId,
    therapist_id: assignment.therapistId || null,
    student_id: assignment.studentId ?? null,
    guest_student_id: assignment.guestStudentId ?? null,
    assignment_date: assignment.assignmentDate,
    start_time: assignment.startTime,
    end_time: assignment.endTime,
    day_of_week: assignment.dayOfWeek,
    assignment_type: assignment.assignmentType,
    activity_name: assignment.activityName || null,
    notes: assignment.notes || null,
    color_override: assignment.colorOverride || null,
  };

  if (assignment.guestStudent) {
    transformed.guest_student = {
      full_name: assignment.guestStudent.fullName?.trim(),
      contact_info: assignment.guestStudent.contactInfo || null,
      notes: assignment.guestStudent.notes || null
    };
  }

  return transformed;
};

// Assignment API
export const assignmentApi = {
  getAll: async (params?: { 
    scheduleId?: number; 
    therapistId?: number; 
    studentId?: number; 
    startDate?: string; 
    endDate?: string;
    page?: number; 
    limit?: number;
  }) => {
    const response = await apiClient.get<{ assignments: any[]; pagination: any }>(`/assignments${params ? '?' + new URLSearchParams(params as any).toString() : ''}`);
    return {
      ...response,
      data: {
        ...response.data,
        assignments: response.data.assignments.map(transformAssignment)
      }
    };
  },
  
  getById: async (id: number) => {
    const response = await apiClient.get<{ assignment: any }>(`/assignments/${id}`);
    return {
      ...response,
      data: {
        assignment: transformAssignment(response.data.assignment)
      }
    };
  },
  
  create: async (data: Partial<Assignment>) => {
    const response = await apiClient.post<{ assignment: any }>('/assignments', transformAssignmentForApi(data));
    return {
      ...response,
      data: {
        assignment: transformAssignment(response.data.assignment)
      }
    };
  },
  
  update: async (id: number, data: Partial<Assignment>) => {
    const response = await apiClient.put<{ assignment: any }>(`/assignments/${id}`, transformAssignmentForApi(data));
    return {
      ...response,
      data: {
        assignment: transformAssignment(response.data.assignment)
      }
    };
  },
  
  delete: (id: number) =>
    apiClient.delete(`/assignments/${id}`),
  
  createBulk: async (assignments: Partial<Assignment>[]) => {
    const response = await apiClient.post<{ created: any[]; errors: any[]; summary: any }>('/assignments/bulk', { 
      assignments: assignments.map(transformAssignmentForApi) 
    });
    return {
      ...response,
      data: {
        ...response.data,
        created: response.data.created.map(transformAssignment)
      }
    };
  },
  
  getConflicts: (params?: { scheduleId?: number; startDate?: string; endDate?: string }) =>
    apiClient.get<{ conflicts: any[]; summary: any }>(`/assignments/conflicts${params ? '?' + new URLSearchParams(params as any).toString() : ''}`),
};

// Document Transmittals API
export const documentTransmittalsApi = {
  getAll: async (params?: { 
    page?: number; 
    limit?: number; 
    status?: string; 
    documentType?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    return apiClient.get<any>(`/document-transmittals${queryString ? '?' + queryString : ''}`);
  },
  
  getById: (id: number) =>
    apiClient.get<any>(`/document-transmittals/${id}`),
  
  create: async (formData: FormData) => {
    // Use axios directly for file upload with FormData
    const response = await api.post('/document-transmittals', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // New: Get document history for student and document type
  getHistory: async (studentId: number, documentType: string) => {
    const queryParams = new URLSearchParams({
      student_id: studentId.toString(),
      document_type: documentType
    });
    return apiClient.get<any>(`/document-transmittals/history?${queryParams.toString()}`);
  },

  // New: Replace an existing document
  replace: async (formData: FormData) => {
    // Use axios directly for file upload with FormData
    const response = await api.post('/document-transmittals/replace', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  updateStatus: (id: number, status: string, emailSentAt?: string) =>
    apiClient.put<any>(`/document-transmittals/${id}/status`, { status, emailSentAt }),
  
  download: async (id: number, fileName: string) => {
    const response = await api.get(`/document-transmittals/${id}/download`, {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Pending Reports API
export const pendingReportsApi = {
  getAll: async (params?: { 
    reportType?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    return apiClient.get<any>(`/pending-reports${queryString ? '?' + queryString : ''}`);
  },
  
  refresh: () =>
    apiClient.post<any>('/pending-reports/refresh'),
  
  reset: (confirmation: string) =>
    apiClient.post<any>('/pending-reports/reset', { confirmation }),
  
  export: async () => {
    const response = await api.get('/pending-reports/export', {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `pending_reports_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  
  getStats: () =>
    apiClient.get<any>('/pending-reports/stats'),
};

// Settings API
export const settingsApi = {
  getSchoolSettings: () =>
    apiClient.get<any>('/settings/school'),
  
  updateSchoolSettings: (data: { school_name: string; email_signature_template?: string }) =>
    apiClient.put<any>('/settings/school', data),
  
  getAllSettings: () =>
    apiClient.get<any>('/settings/all'),
  
  getSetting: (key: string) =>
    apiClient.get<any>(`/settings/${key}`),
  
  updateSetting: (key: string, data: { value: any; type?: string; description?: string }) =>
    apiClient.put<any>(`/settings/${key}`, data),
};

// Health check
export const healthApi = {
  check: () => apiClient.get<{ message: string; status: string; timestamp: string }>('/health'),
};

export default api;
